import {
    type AxiosError,
    type AxiosResponse,
    isAxiosError,
    type RawAxiosRequestHeaders,
} from 'axios';
import { mapValues, omit } from 'remeda';
import { type z, ZodError } from 'zod';

import {
    defaultHeaders,
    fileUploadHeaders,
    loginHeaders,
    patchDefaultHeaders,
} from '@@api/headers';
import { etagCache } from '@@api/queryClient';
import { HTTP_STATUS_CODES } from '@@constants/http';
import { addDatadogError } from '@@scripts/utils/dataDog';
import { extractETag } from '@@scripts/utils/eTagUtils';

import { ClientValidationError } from './ClientValidationError';
import {
    type AxiosResponseUnion,
    type ContentType,
    type EndpointConfig,
    type EndpointData,
    type ErrorStatusCode,
    type Methods,
    type StatusCode,
    type SuccessResponses,
    type SuccessStatusCode,
} from './types';

/**
 * Normalize eTag and content-type headers
 * @param method The HTTP method being used.
 * @param headers The headers to process.
 * @param path The path of the request.
 * @returns The processed headers.
 */
export const normalizeHeaders = (
    method: 'GET' | 'HEAD' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
    headers: Record<string, unknown> | undefined | null,
    path: string,
) => {
    if (typeof headers !== 'object' || headers === null) {
        return {};
    }

    let processedHeaders = headers;

    // When using the Fetch API, if the body is a FormData object, the browser will
    // automatically set the correct content-type header for us. If we try to set it
    // ourselves, the browser will throw an error.
    if ('content-type' in headers && headers['content-type'] === 'multipart/form-data') {
        processedHeaders = omit(headers, ['content-type']);
    }

    if (etagCache.has(path)) {
        processedHeaders = {
            ...processedHeaders,
            'if-match': etagCache.get(path),
        };
    }

    // We also want to remove the etag header for all requests except GET requests
    // Since headers are part of the cache, we need to remove it before reusing the data
    if (method !== 'GET') {
        processedHeaders = omit(processedHeaders, ['etag']);
    }

    return processedHeaders;
};

/**
 * We don't want to JSON.stringify FormData or URLSearchParams objects
 * as they need to be processed correctly by the fetch API
 * @param body The request body to normalize.
 * @returns The normalized request body.
 */
export const normalizeBody = (body: unknown) => {
    if (
        !body ||
        body instanceof FormData ||
        body instanceof URLSearchParams ||
        typeof body === 'string'
    ) {
        return body;
    }

    return JSON.stringify(body);
};

/**
 * Normalizes query parameters before sending them to the server. This will:
 * - turn every array into a comma-separated string
 * - remove any null, undefined, empty string or empty array values
 * @param query The query parameters to normalize.
 * @returns The normalized query parameters.
 */
export const normalizeQuery = (query?: Record<string, unknown>) => {
    if (!query) {
        return {};
    }
    const newValues = mapValues(query, (value) => {
        if (Array.isArray(value)) {
            if (value.length === 0) {
                return undefined;
            }

            return value.join(',');
        }

        if (value === '' || value === null) {
            return undefined;
        }

        return value;
    });

    return newValues;
};

/**
 * This helper function builds a URL path by replacing path parameters with their corresponding values.
 * @param path The URL path with parameters.
 * @param params An object containing the parameter values.
 * @returns The constructed URL path.
 */
export const buildUrl = (path: string, params?: unknown) => {
    if (!params || typeof params !== 'object') {
        return path;
    }

    return path.replace(/:([A-Za-z_]+)\??/g, (_match, rawKey) => {
        const key = rawKey as keyof typeof params;
        const value = params[key];

        if (value === undefined || value === null || value === '') {
            throw new Error(`Missing value for path parameter ":${String(key)}" in "${path}"`);
        }

        return encodeURIComponent(String(value));
    });
};

/**
 * This helper function normalizes the request headers.
 * @param headers The headers to normalize.
 * @returns The headers with default headers.
 */
export const normalizeHeadersData = <
    Method extends Methods,
    Path extends string,
    Query extends z.ZodObject | undefined,
    Headers extends z.ZodObject | undefined,
    Body extends z.ZodType | undefined,
    Responses extends Partial<Record<StatusCode, z.ZodType>>,
>(
    config: EndpointConfig<Method, Path, Query, Headers, Body, Responses>,
    headers: Record<string, unknown> | undefined,
) => {
    if (config.method === 'PATCH') {
        return {
            ...patchDefaultHeaders(),
            ...headers,
        };
    }

    if (config.method === 'POST' && config.contentType === 'multipart/form-data') {
        return fileUploadHeaders();
    }

    if (config.method === 'POST' && config.contentType === 'application/x-www-form-urlencoded') {
        return loginHeaders();
    }

    return {
        ...defaultHeaders(),
        ...headers,
    };
};

export const handleSuccessResponse = async <
    Method extends Methods,
    Path extends string,
    Query extends z.ZodObject | undefined,
    Headers extends z.ZodObject | undefined,
    Body extends z.ZodType | undefined,
    Responses extends Partial<Record<SuccessStatusCode, z.ZodType>>,
>(
    result: AxiosResponse | undefined,
    config: EndpointConfig<Method, Path, Query, Headers, Body, Responses>,
) => {
    if (!result) {
        const fallbackError = {
            headers: new Headers(),
            status: HTTP_STATUS_CODES.NO_RESPONSE,
            body: { message: 'Fetcher Error: No response', path: config.path },
        };

        // We do this because it's gonna be wrapped later by an actual error
        throw fallbackError;
    }

    const headers = new Headers(result.headers as Record<string, string>);

    const etagHeader = headers.get('etag');
    const status = result.status as SuccessStatusCode; // We assert StatusCode because axios defaults to `number`

    if (etagHeader) {
        etagCache.set(config.path, extractETag(etagHeader));
    }

    const contentType = headers.get('content-type') as ContentType;

    if (contentType?.includes('application/') && contentType?.includes('json')) {
        const response = {
            status,
            body: result.data,
            headers,
        };

        const responseSchema = config.responses[response.status];

        try {
            const resultResponse = responseSchema?.parse(response.body);

            return {
                ...response,
                headers,
                body: resultResponse,
            };
        } catch (error: unknown) {
            if (error instanceof ZodError) {
                const errorInstance = new ClientValidationError(
                    'Response',
                    config,
                    error,
                    response.body,
                );

                addDatadogError(errorInstance);

                throw errorInstance;
            }
        }

        return response;
    }

    if (contentType?.includes('text/')) {
        return {
            status,
            body: await result.data,
            headers,
        };
    }

    const data = await result.data;

    return {
        status,
        body: data,
        headers,
    };
};

export const handleErrorResponse = async <
    Method extends Methods,
    Path extends string,
    Query extends z.ZodObject | undefined,
    Headers extends z.ZodObject | undefined,
    Body extends z.ZodType | undefined,
    Responses extends Partial<Record<ErrorStatusCode, z.ZodType>>,
>(
    result: AxiosResponse | undefined,
    config: EndpointConfig<Method, Path, Query, Headers, Body, Responses>,
) => {
    if (!result) {
        return {
            headers: new Headers(),
            status: HTTP_STATUS_CODES.NO_RESPONSE,
            body: { message: 'Fetcher Error: No response', path: config.path },
        };
    }

    const headers = new Headers(result.headers as Record<string, string>);

    const etagHeader = headers.get('etag');
    const status = result.status as ErrorStatusCode; // We assert StatusCode because axios defaults to `number`

    if (etagHeader) {
        etagCache.set(config.path, extractETag(etagHeader));
    }

    if (result.config.method?.toUpperCase() === 'HEAD' && status === HTTP_STATUS_CODES.NOT_FOUND) {
        return {
            status,
            body: { message: 'tag.form.asyncvalidate.reject' },
            headers,
        };
    }

    const contentType = headers.get('content-type') as ContentType;

    if (contentType?.includes('application/') && contentType?.includes('json')) {
        const response = {
            status,
            body: result.data,
            headers,
        };

        const responseSchema = config.responses[response.status];

        try {
            const resultResponse = responseSchema?.parse(response.body);

            return {
                ...response,
                headers,
                body: resultResponse,
            };
        } catch (error: unknown) {
            if (error instanceof ZodError) {
                const errorInstance = new ClientValidationError(
                    'Response',
                    config,
                    error,
                    response.body,
                );

                addDatadogError(errorInstance);

                throw errorInstance;
            }
        }

        return response;
    }

    if (contentType?.includes('text/')) {
        return {
            status,
            body: await result.data,
            headers,
        };
    }

    const data = await result.data;

    return {
        status,
        body: data,
        headers,
    };
};

const validateBody = <
    Method extends Methods,
    Path extends `/${string}`,
    Query extends z.ZodObject | undefined,
    Headers extends z.ZodObject | undefined,
    Body extends z.ZodType | undefined,
    Responses extends Partial<Record<StatusCode, z.ZodType>>,
>(
    config: EndpointConfig<Method, Path, Query, Headers, Body, Responses>,
    data: EndpointData<EndpointConfig<Method, Path, Query, Headers, Body, Responses>>,
) => {
    const result = config.body?.safeParse(data.body) || {
        success: true,
        data: data.body,
    };

    if (!result.success) {
        const errorInstance = new ClientValidationError('Body', config, result.error, data.body);

        addDatadogError(errorInstance);

        throw errorInstance;
    }

    return result;
};

export const processBody = <
    Method extends Methods,
    Path extends `/${string}`,
    Query extends z.ZodObject | undefined,
    Headers extends z.ZodObject | undefined,
    Body extends z.ZodType | undefined,
    Responses extends Partial<Record<StatusCode, z.ZodType>>,
>(
    config: EndpointConfig<Method, Path, Query, Headers, Body, Responses>,
    data: EndpointData<EndpointConfig<Method, Path, Query, Headers, Body, Responses>>,
) => {
    const validatedBody = validateBody(config, data);
    const normalizedBody = normalizeBody(validatedBody.data);

    return normalizedBody;
};

const validateQuery = <
    Method extends Methods,
    Path extends `/${string}`,
    Query extends z.ZodObject | undefined,
    Headers extends z.ZodObject | undefined,
    Body extends z.ZodType | undefined,
    Responses extends Partial<Record<StatusCode, z.ZodType>>,
>(
    config: EndpointConfig<Method, Path, Query, Headers, Body, Responses>,
    data: EndpointData<EndpointConfig<Method, Path, Query, Headers, Body, Responses>>,
) => {
    const result = ('query' in config && config.query?.safeParse(data.query)) || {
        success: true,
        data: data.query,
    };

    if (!result.success) {
        const errorInstance = new ClientValidationError('Query', config, result.error, data.query);

        addDatadogError(errorInstance);

        throw errorInstance;
    }

    return result;
};

export const processQuery = <
    Method extends Methods,
    Path extends `/${string}`,
    Query extends z.ZodObject | undefined,
    Headers extends z.ZodObject | undefined,
    Body extends z.ZodType | undefined,
    Responses extends Partial<Record<StatusCode, z.ZodType>>,
>(
    config: EndpointConfig<Method, Path, Query, Headers, Body, Responses>,
    data: EndpointData<EndpointConfig<Method, Path, Query, Headers, Body, Responses>>,
) => {
    const validatedQuery = validateQuery(config, data);
    const normalizedQuery = normalizeQuery(validatedQuery.data);

    return normalizedQuery;
};

const validateHeaders = <
    Method extends Methods,
    Path extends `/${string}`,
    Query extends z.ZodObject | undefined,
    Headers extends z.ZodObject | undefined,
    Body extends z.ZodType | undefined,
    Responses extends Partial<Record<StatusCode, z.ZodType>>,
>(
    config: EndpointConfig<Method, Path, Query, Headers, Body, Responses>,
    data: ReturnType<typeof normalizeHeadersData<Method, Path, Query, Headers, Body, Responses>>,
) => {
    const resultHeaders = ('headers' in config && config.headers?.safeParse(data)) || {
        success: true,
        data,
    };

    if (!resultHeaders.success) {
        const errorInstance = new ClientValidationError(
            'Headers',
            config,
            resultHeaders.error,
            data,
        );

        addDatadogError(errorInstance);

        throw errorInstance;
    }

    return resultHeaders;
};

export const processHeaders = <
    Method extends Methods,
    Path extends `/${string}`,
    Query extends z.ZodObject | undefined,
    Headers extends z.ZodObject | undefined,
    Body extends z.ZodType | undefined,
    Responses extends Partial<Record<StatusCode, z.ZodType>>,
>(
    config: EndpointConfig<Method, Path, Query, Headers, Body, Responses>,
    data: EndpointData<EndpointConfig<Method, Path, Query, Headers, Body, Responses>>,
    recomposedPath: string,
) => {
    const normalizedHeadersData = normalizeHeadersData(config, data.headers);
    const validatedHeaders = validateHeaders(config, normalizedHeadersData);

    const processedHeaders = normalizeHeaders(config.method, validatedHeaders.data, recomposedPath);

    return processedHeaders as RawAxiosRequestHeaders;
};

export const processSuccessResponse = <
    Method extends Methods,
    Path extends `/${string}`,
    Query extends z.ZodObject | undefined,
    Headers extends z.ZodObject | undefined,
    Body extends z.ZodType | undefined,
    Responses extends Partial<Record<StatusCode, z.ZodType>>,
>(
    config: EndpointConfig<Method, Path, Query, Headers, Body, Responses>,
    result: AxiosResponse<AxiosResponseUnion<Responses>>,
) => {
    // This shouldn't be necessary but in some cases, the server doesn't respond at all
    if (!result) {
        const fallbackError = {
            headers: new Headers(),
            status: HTTP_STATUS_CODES.NO_RESPONSE,
            body: {
                message: 'Fetcher Error: No response',
                path: config.path,
            },
        };

        // We do this because it's gonna be wrapped later by an actual error
        throw fallbackError;
    }

    // We assert StatusCode because axios defaults to `number`
    const status = result.status as SuccessStatusCode;

    const headers = new Headers(result.headers as Record<string, string>);
    const etagHeader = headers.get('etag');

    if (etagHeader) {
        etagCache.set(result.config.url, extractETag(etagHeader));
    }

    const contentType = headers.get('content-type') as ContentType;

    if (contentType?.includes('application/') && contentType?.includes('json')) {
        const response = {
            status,
            body: result.data,
            headers,
        };

        const responseSchema = config.responses[response.status];

        const resultResponse = responseSchema?.safeParse(response.body);

        if (!resultResponse) {
            // We need to decide what to do in this scenario.
            // The previous client was just returning the unparsed response
            // which is wrong so we should fix it. But fixing it now might introduce
            // new errors in the app.
            // This is what we should do:
            // throw new Error('No response schema found');
            console.error('No response schema found for status', response.status, 'in', config);

            return response as SuccessResponses<Responses>;
        }

        if (!resultResponse.success) {
            const errorInstance = new ClientValidationError(
                'Response',
                config,
                resultResponse.error,
                response.body,
            );

            addDatadogError(errorInstance);

            throw errorInstance;
        }

        return {
            status: response.status,
            headers,
            body: resultResponse.data,
        } as SuccessResponses<Responses>;
    }

    return {
        status,
        body: result.data,
        headers,
    } as SuccessResponses<Responses>;
};

export const processErrorResponse = async <
    Method extends Methods,
    Path extends `/${string}`,
    Query extends z.ZodObject | undefined,
    Headers extends z.ZodObject | undefined,
    Body extends z.ZodType | undefined,
    Responses extends Partial<Record<StatusCode, z.ZodType>>,
>(
    config: EndpointConfig<Method, Path, Query, Headers, Body, Responses>,
    error: unknown,
) => {
    if (isAxiosError(error)) {
        const axiosError = error as AxiosError;
        const response = axiosError.response;

        const handledResponse = await handleErrorResponse<
            Method,
            Path,
            Query,
            Headers,
            Body,
            Responses
        >(response, config);
        throw handledResponse;
    }

    const finalResult = {
        headers: new Headers(),
        status: HTTP_STATUS_CODES.BAD_REQUEST,
        body: { message: `Fetcher Error: ${error?.toString?.() || error}` },
    };

    // We do this because it's gonna be wrapped later by an actual error
    throw finalResult;
};
